import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import {combineLatest, Observable, ReplaySubject} from 'rxjs';
import { map, shareReplay, startWith, switchMap, tap, filter } from 'rxjs/operators';
import { SideNavigationItem } from '../atlas_sdk/api';
import { BusinessNavConfigService } from '../config.service';
import { ModalService } from '../core-components/modal/modal.service';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { BusinessNavDataService } from '../data.service';
import { SalesInfoModalComponent } from '../modals/sales-info/sales-info.modal.component';
import { AtlasTranslateService } from '../translate/translate.service';
import { ChangeSubscriptionModalComponent } from '../modals/change-subscription/change-subscription.modal.component';

// nav item ids that we want above the list of products
const NAV_IDS_ABOVE_PRODUCTS_LIST = [
        // Business
        'nav-get-started',
        'nav-dashboard',
        'nav-executive-report',
        'nav-inbox',
        // Brand (multi-location)
        'nav-brand-dashboard',
        'nav-brand-report',
        'nav-brand-reputation-overview',
        'nav-brand-reputation-manage',
        'nav-brand-reputation-insights',
        'nav-brand-listings-overview',
        'nav-brand-listings-manage',
        'nav-brand-google-my-business',
        'nav-brand-social',
        'nav-brand-advertising',
        'nav-brand-data-export'
    ];
// nav item ids that we want placed with products (at the bottom)
const NAV_IDS_WITH_PRODUCTS_LIST = [
        'nav-store',
        'nav-products'
    ];

@Component({
  selector: 'atlas-business-sidebar',
  templateUrl: './business-sidebar.component.html',
  styleUrls: ['./business-sidebar.component.scss']
})
export class BusinessSidebarComponent {
  public isBrandType$: Observable<boolean>;
  public businessCenterName$: Observable<string>;
  public partnerName$: Observable<string>;
  public marketName$: Observable<string>;
  public logoUrl$: Observable<string>;
  public coBrandingUrl$: Observable<string>;
  public contactMessage$: Observable<string>;
  public salespersonPictureUrl$: Observable<string>;
  public navigationItems$: Observable<SideNavigationItem[]>;
  public topBusinessNavigationItems$: Observable<SideNavigationItem[]>;
  public productBusinessNavigationItems$: Observable<SideNavigationItem[]>;
  public storeBusinessNavigationItems$: Observable<SideNavigationItem[]>;
  public bottomBusinessNavigationItems$: Observable<SideNavigationItem[]>;
  public mobile$: Observable<boolean>;
  public showCancellationModal$: Observable<boolean>;
  public businessAppBranding$: Observable<boolean>;
  logoHeight$$: ReplaySubject<number> = new ReplaySubject<number>(1);
  logoHeight$: Observable<number>;
  @ViewChild('injectionSpace', { static: true }) injectionSpace: ElementRef;

  constructor(private apiService: BusinessNavDataService,
              private cfgService: BusinessNavConfigService,
              private modalService: ModalService,
              private sidebarService: SidebarService,
              private cdr: ChangeDetectorRef,
              private t: AtlasTranslateService,
  ) {

    this.logoHeight$ = this.logoHeight$$.asObservable().pipe(
      tap((height: number) => {
        this.rememberLastLogoHeight(height);
      }),
      startWith(this.getLastLogoHeight()),
      shareReplay(1),
    );

    this.coBrandingUrl$ = this.apiService.cobrandingLogoURL$;

    this.isBrandType$ = this.cfgService.isBrandType$;
    this.navigationItems$ = combineLatest(this.apiService.businessNavigationItems$, this.apiService.brandNavigationItems$, this.isBrandType$)
      .pipe(
        map(([businessNavigationItems, brandNavigationItems, isBrandType]: [SideNavigationItem[], SideNavigationItem[], boolean]) => isBrandType ? brandNavigationItems : businessNavigationItems),
        shareReplay(1)
      );
    this.storeBusinessNavigationItems$ = this.navigationItems$.pipe(
      filter(items => !!items),
      map(items => {
        return items.filter(item => {
          return item.sideNavigationLink && NAV_IDS_WITH_PRODUCTS_LIST.indexOf(item.sideNavigationLink.navigationId) > -1;
        });
      }),
      startWith([])
    );
    this.productBusinessNavigationItems$ = this.navigationItems$.pipe(
      filter(items => !!items),
      map(items => {
        return items.filter(item => {
          return (
            item.sideNavigationLink
            && item.sideNavigationLink.navigationId.startsWith('product-')
          );
        });
      }),
      startWith([])
    );
    this.bottomBusinessNavigationItems$ = this.navigationItems$.pipe(
      filter(items => !!items),
      map(items => {
        return items.filter(item => {
          return !(
            item.sideNavigationLink
            && (item.sideNavigationLink.navigationId.startsWith('product-')
                || NAV_IDS_ABOVE_PRODUCTS_LIST.indexOf(
                  item.sideNavigationLink.navigationId
                ) > -1
                || NAV_IDS_WITH_PRODUCTS_LIST.indexOf(
                  item.sideNavigationLink.navigationId
                ) > -1)
          ) && !(
            item.sideNavigationContainer
            && NAV_IDS_ABOVE_PRODUCTS_LIST.indexOf(
              item.sideNavigationContainer.sideNavigationItems[0].sideNavigationLink.navigationId
            ) > -1
          );
        });
      }),
      startWith([])
    );
    this.topBusinessNavigationItems$ = this.navigationItems$.pipe(
      filter(items => !!items),
      map(items => {
        return items.filter(item => {
          return (
            item.sideNavigationLink
            && NAV_IDS_ABOVE_PRODUCTS_LIST.indexOf(
                item.sideNavigationLink.navigationId
              ) > -1
          ) || (
            item.sideNavigationContainer
            && NAV_IDS_ABOVE_PRODUCTS_LIST.indexOf(
              item.sideNavigationContainer.sideNavigationItems[0].sideNavigationLink.navigationId
            ) > -1
          );
        });
      }),
      startWith([])
    );
    this.mobile$ = this.sidebarService.mobile$;
    this.businessCenterName$ = this.apiService.businessCenterName$;
    this.partnerName$ = this.apiService.partnerName$;
    this.marketName$ = this.apiService.marketName$;
    this.logoUrl$ = this.apiService.marketLogoUrl$;
    this.salespersonPictureUrl$ = this.apiService.salespersonPictureUrl$;
    this.showCancellationModal$ = this.apiService.retentionConfig$.pipe(map(r => !!r.cancellationNotificationEmail));
    this.businessAppBranding$ = this.apiService.businessAppBranding$;

    this.contactMessage$ = this.apiService.salespersonName$.pipe(
      startWith('Us'),
      switchMap((name: string) => {
        if (name === 'Us') {
          return this.t.stream('COMMON.ACTION_LABELS.CONTACT_US', 'Contact Us');
        }
        return this.t.stream('COMMON.CONTACT_SALESPERSON', 'Contact ' + name).pipe(
          map((message: string) => message.replace('{{ salesPersonName }}', name)),
        );
      })
    );
  }

  logoLoaded(logo: { clientHeight: number }): void {
    setTimeout(() => {
      this.logoHeight$$.next(Math.min(logo.clientHeight, 120));
    }, 0);
  }

  openAssistanceModal(): void {
    this.modalService.open(SalesInfoModalComponent);
  }

  openChangeSubscriptionModal(): void {
    this.modalService.open(ChangeSubscriptionModalComponent);
  }

  private rememberLastLogoHeight(height: number): void {
    localStorage.setItem('business-sidenav-logo-height', '' + height);
  }

  private getLastLogoHeight(): number {
    const t = localStorage.getItem('business-sidenav-logo-height');
    if (!t) {
      return 80;
    }
    return +t;
  }
}
